import React, { useState } from 'react';
import { ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@i18n/apiError';
import { useRequestMagicLink } from '@hooks/auth/useMagicLink';
import { useStoreDetails } from '@hooks/portal/useStoreDetails';
import {
  CustomerPortalLogoSizeInput,
  getCustomerPortalLogoSizeStyles,
} from '@lib/customerPortalLogoSize';
import EmailSentConfirmation from './email-confirmation';

interface CustomStyles {
  primaryBackgroundColor: string | null;
  secondaryBackgroundColor: string | null;
  primaryTextColor: string | null;
  secondaryTextColor: string | null;
  buttonBackgroundColor: string | null;
  buttonTextColor: string | null;
  logo: string | null;
  logoSize: CustomerPortalLogoSizeInput;
}

const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`skeleton-pulse rounded ${className}`} />
);

const LoadingSkeleton: React.FC = () => (
  <div className="flex justify-center items-center min-h-screen bg-white px-4 sm:px-8">
    <div className="w-full max-w-[500px] bg-white p-6 sm:p-8 rounded-[8px] shadow-[0px_4px_20px_rgba(0,0,0,0.08)]">
      <div className="flex justify-center mb-6">
        <Skeleton className="w-[180px] h-[60px]" />
      </div>
      <div className="text-center mb-6">
        <Skeleton className="w-[70%] h-[40px] mx-auto mb-3" />
        <Skeleton className="w-[85%] h-[24px] mx-auto" />
      </div>
      <div className="mt-6">
        <Skeleton className="w-[35%] h-[24px] mb-3" />
        <Skeleton className="w-full h-[56px] mb-4" />
        <Skeleton className="w-[80%] h-[20px] mb-6" />
        <Skeleton className="w-full h-[48px]" />
      </div>
    </div>
  </div>
);

const StoreNotFound: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col justify-center items-center min-h-screen bg-[#f9fafb] px-6 text-center">
      <ShoppingBag className="w-20 h-20 text-[#9ca3af] mb-4" />
      <h2 className="text-[20px] font-bold text-[#111827] mb-3">{t('auth.storeNotFound.title')}</h2>
      <p className="text-[16px] text-[#6b7280] max-w-[400px]">{t('auth.storeNotFound.body')}</p>
    </div>
  );
};

const MagicLinkRequest: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [showEmailSent, setShowEmailSent] = useState(false);
  const [requestEmail, setRequestEmail] = useState('');

  // VITE_PORTAL_DOMAIN lets local dev resolve a real store while running on localhost.
  // Unset in prod/CI, so behavior is identical to window.location.hostname there.
  const domain =
    import.meta.env.VITE_PORTAL_DOMAIN ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  const storeQuery = useStoreDetails(domain);
  const requestMagicLinkMutation = useRequestMagicLink();
  const customStyles: CustomStyles | undefined = storeQuery.data?.styles ?? undefined;
  const isLoadingStore = storeQuery.isPending;
  const storeNotFound = storeQuery.data?.notFound === true || storeQuery.isError;

  const s = {
    primaryBg: customStyles?.primaryBackgroundColor ?? '#FFFFFF',
    secondaryBg: customStyles?.secondaryBackgroundColor ?? '#F8F9FA',
    primaryTxt: customStyles?.primaryTextColor ?? '#000000',
    secondaryTxt: customStyles?.secondaryTextColor ?? '#6B7280',
    btnBg: customStyles?.buttonBackgroundColor ?? '#2563EB',
    btnTxt: customStyles?.buttonTextColor ?? '#FFFFFF',
    logo: customStyles?.logo ?? null,
    logoSize: customStyles?.logoSize,
  };

  const validateEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = email.trim();
    if (!trimmed) {
      setError(t('auth.login.emailRequired'));
      return;
    }
    if (!validateEmail(trimmed)) {
      setError(t('auth.login.emailInvalid'));
      return;
    }

    requestMagicLinkMutation.mutate(
      { email: trimmed, domain },
      {
        onSuccess: () => {
          setRequestEmail(trimmed);
          setShowEmailSent(true);
        },
        onError: (err) => setError(apiErrorMessage(err)),
      },
    );
  };

  const isSubmitting = requestMagicLinkMutation.isPending;

  if (isLoadingStore) return <LoadingSkeleton />;
  if (storeNotFound) return <StoreNotFound />;
  if (showEmailSent)
    return (
      <EmailSentConfirmation
        email={requestEmail}
        magicLinkEmail={requestEmail}
        customStyles={customStyles}
        domain={domain}
        isLoading={false}
      />
    );

  return (
    <div
      className="flex justify-center items-center min-h-screen px-4 sm:px-8 transition-all duration-300"
      style={{ backgroundColor: s.primaryBg }}
    >
      <div
        className="w-full max-w-[500px] p-6 sm:p-8 rounded-[8px] transition-all duration-300"
        style={{
          backgroundColor: s.secondaryBg,
          boxShadow: '13px 10px 39px -3px rgba(0,0,0,0.1)',
        }}
      >
        <div className="flex justify-center mb-6">
          <img
            src={s.logo ?? '/assets/images/logos/lasso-new-logo-white-transparentbg.png'}
            alt={t('common.logoAlt')}
            style={{
              ...getCustomerPortalLogoSizeStyles(s.logoSize),
              objectFit: 'contain',
              maxWidth: '100%',
              filter: s.logo ? 'none' : 'invert(1)',
            }}
          />
        </div>

        <h1
          className="primary-font-medium text-center text-[20px] sm:text-[24px] font-bold mb-2"
          style={{ color: s.primaryTxt }}
        >
          {t('auth.login.title')}
        </h1>
        <p
          className="primary-font-medium text-center text-[14px] sm:text-[16px] mb-6"
          style={{ color: s.secondaryTxt }}
        >
          {t('auth.login.subtitle')}
        </p>

        <form onSubmit={handleSubmit}>
          <label
            className="block text-[13px] sm:text-[14px] mb-1 primary-font-regular"
            style={{ color: s.secondaryTxt }}
          >
            {t('auth.login.emailLabel')}
          </label>
          <input
            type="email"
            value={email}
            placeholder={t('auth.login.emailPlaceholder')}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            required
            className="w-full px-3 py-3 rounded border text-[14px] outline-none transition-colors mb-1"
            style={{
              borderColor: error ? '#DC2626' : s.secondaryTxt,
              color: s.primaryTxt,
              backgroundColor: 'transparent',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = s.btnBg)}
            onBlur={(e) => (e.currentTarget.style.borderColor = error ? '#DC2626' : s.secondaryTxt)}
          />

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded px-3 py-2 mt-2 mb-2">
              <p className="text-red-700 text-[12px]">{error}</p>
            </div>
          )}

          <p
            className="primary-font-medium text-[12px] sm:text-[13px] mb-6 mt-2"
            style={{ color: s.secondaryTxt }}
          >
            {t('auth.login.linkNotice')}
          </p>

          <button
            type="submit"
            disabled={isSubmitting || !email.trim()}
            className="w-full py-3 rounded text-[14px] sm:text-[16px] font-medium capitalize transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: s.btnBg, color: s.btnTxt }}
          >
            {isSubmitting ? t('common.sending') : t('auth.login.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MagicLinkRequest;
